import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Bar,
  BarChart,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import Notification, { NotificationContainer } from '../../../components/Notification';
import contractsService from '../../../services/compliance/contracts.service';
import { ContractDashboardResponse } from '../../../types/compliance';
import NeedsAttentionPanel from './NeedsAttentionPanel';

const STATUS_BADGE: Record<string, string> = {
  draft: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  active: 'bg-green-200 text-green-800 dark:bg-green-900 dark:text-green-200',
  pending_renewal: 'bg-amber-200 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  expired: 'bg-red-200 text-red-800 dark:bg-red-900 dark:text-red-200',
  terminated: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
};

const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  active: 'Active',
  pending_renewal: 'Pending Renewal',
  expired: 'Expired',
  terminated: 'Terminated',
};

const STATUS_BAR: Record<string, string> = {
  draft: 'bg-gray-400',
  active: 'bg-green-500',
  pending_renewal: 'bg-amber-500',
  expired: 'bg-red-500',
  terminated: 'bg-gray-400',
};

const PIE_COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#f97316', '#6366f1'];

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 ${className}`}
    >
      {children}
    </div>
  );
}

function KpiCard({
  label,
  value,
  accent,
  subtitle,
  accentBar,
}: {
  label: string;
  value: React.ReactNode;
  accent?: string;
  subtitle?: string;
  accentBar?: string;
}) {
  return (
    <Card className="flex flex-col h-full">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted text-center">
        {label}
      </div>
      <div
        className={`text-3xl font-bold text-center mt-2 ${accent || 'text-body'}`}
      >
        {value}
      </div>
      {subtitle && (
        <div className="text-xs text-center mt-1 text-muted">
          {subtitle}
        </div>
      )}
      {accentBar && <div className={`h-1 mt-3 rounded ${accentBar}`} />}
    </Card>
  );
}

function formatValue(v: number | null | undefined): string {
  if (v === null || v === undefined) return '';
  const n = Number(v);
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

interface Props {
  /** When rendered inside ContractsPage's tab strip, hide the standalone
   *  page title + its cross-nav links so the tab header is the only
   *  header a user sees. */
  embedded?: boolean;
}


export default function ContractDashboard({ embedded = false }: Props = {}) {
  const navigate = useNavigate();
  const [isDark, setIsDark] = useState<boolean>(() => document.documentElement.classList.contains('dark'));
  useEffect(() => {
    const check = () => setIsDark(document.documentElement.classList.contains('dark'));
    check();
    const observer = new MutationObserver(() => check());
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  const [dashboard, setDashboard] = useState<ContractDashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [department, setDepartment] = useState<string>('');
  const [errorNotification, setErrorNotification] =
    useState<{ title: string; message: string } | null>(null);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const data = await contractsService.getDashboard(department || undefined);
      setDashboard(data);
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error';
      setErrorNotification({ title: 'Failed to load dashboard', message: msg });
    } finally {
      setLoading(false);
    }
  }, [department]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const statusPieData = useMemo(() => {
    if (!dashboard) return [];
    return dashboard.status_breakdown.map((b) => ({
      name: STATUS_LABEL[b.status] ?? b.status,
      value: b.count,
      key: b.status,
    }));
  }, [dashboard]);

  const categoryChartData = useMemo(() => {
    if (!dashboard) return [];
    return dashboard.category_series.map((row) => ({
      category: row.category || '(none)',
      value: row.total_value,
      count: row.count,
    }));
  }, [dashboard]);

  const monthlyChartData = useMemo(() => {
    if (!dashboard) return [];
    return dashboard.monthly_expiring.map((row) => ({
      month: row.month,
      count: row.count,
    }));
  }, [dashboard]);

  const ownerChartData = useMemo(() => {
    if (!dashboard) return [];
    return dashboard.owner_series.map((row) => ({
      owner: row.owner_display,
      count: row.count,
    }));
  }, [dashboard]);

  if (loading && !dashboard) {
    return (
      <div className="p-6">
        <div className="text-muted">Loading dashboard…</div>
      </div>
    );
  }
  if (!dashboard) return null;

  const statusCount = Object.fromEntries(
    dashboard.status_breakdown.map((b) => [b.status, b.count]),
  );

  return (
    <div className="p-6 space-y-6">
      <NotificationContainer>
        {errorNotification && (
          <Notification
            type="error"
            title={errorNotification.title}
            message={errorNotification.message}
            onClose={() => setErrorNotification(null)}
          />
        )}
      </NotificationContainer>

      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          {!embedded && (
            <h1 className="text-2xl font-bold text-body">
              Contracts Dashboard
            </h1>
          )}
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 dark:text-gray-300">
            Department
          </label>
          <select
            className="text-sm px-2 py-1 border rounded bg-panel border-gray-300 dark:border-gray-600 text-body"
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
          >
            <option value="">All</option>
            {dashboard.departments.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          {!embedded && (
            <Link
              to="/compliance/contracts"
              className="text-sm px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
            >
              View All Contracts
            </Link>
          )}
        </div>
      </div>

      {/* The signed-in user's own to-do list, above the company-wide numbers */}
      <NeedsAttentionPanel />

      {/* Renewal action alert banner */}
      {dashboard.renewal_action_soon.length > 0 && (
        <div className="border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-900/30 p-4 rounded">
          <div className="font-semibold text-amber-900 dark:text-amber-200">
            {dashboard.renewal_action_soon.length} auto-renewing
            {' '}contract{dashboard.renewal_action_soon.length === 1 ? '' : 's'}{' '}
            need action in the next 30 days.
          </div>
          <ul className="mt-2 text-sm text-amber-900 dark:text-amber-100 space-y-1">
            {dashboard.renewal_action_soon.slice(0, 5).map((r) => (
              <li key={r.id}>
                <Link
                  to={`/compliance/contracts?id=${r.id}`}
                  className="underline hover:opacity-80"
                >
                  {r.title}
                </Link>
                {' — '}
                <span className="opacity-80">
                  {r.third_party_name} · action by {r.renewal_action_date}
                </span>
              </li>
            ))}
            {dashboard.renewal_action_soon.length > 5 && (
              <li className="opacity-70">
                …and {dashboard.renewal_action_soon.length - 5} more.
              </li>
            )}
          </ul>
        </div>
      )}

      {/* KPI row — each drills into the contracts list pre-filtered */}
      <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <Link to="/compliance/contracts?scope=all&status=active" className="h-full">
          <KpiCard
            label="Total Active"
            value={dashboard.kpis.total_active}
            accent="text-green-700 dark:text-green-300"
          />
        </Link>
        <KpiCard
          label="Total Contract Value"
          value={dashboard.kpis.total_value_display}
          accent="text-blue-700 dark:text-blue-300"
          subtitle="Active contracts only"
        />
        <KpiCard
          label="Monthly Cost"
          value={dashboard.kpis.monthly_cost_display}
          accent="text-emerald-700 dark:text-emerald-300"
          subtitle="Active contracts only"
        />
        <Link to={`/compliance/contracts?scope=all&status=active&ending_before=${dashboard.in_90}`} className="h-full">
          <KpiCard
            label="Value Expiring in 90 Days"
            value={dashboard.kpis.expiring_90_value_display}
            accent="text-amber-700 dark:text-amber-300"
          />
        </Link>
        <Link to={`/compliance/contracts?scope=all&status=active&ending_before=${dashboard.in_30}`} className="h-full">
          <KpiCard
            label="Expiring in 30 Days"
            value={dashboard.kpis.expiring_30}
            accent={dashboard.kpis.expiring_30 > 0 ? 'text-red-700 dark:text-red-300' : undefined}
          />
        </Link>
        <Link to="/compliance/contracts?scope=all&status=pending_renewal" className="h-full">
          <KpiCard
            label="Pending Renewal"
            value={dashboard.kpis.pending_renewal}
            accent="text-indigo-700 dark:text-indigo-300"
          />
        </Link>
      </div>

      {/* Status breakdown row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {(['draft', 'active', 'pending_renewal', 'expired', 'terminated'] as const).map((s) => (
          <Link key={s} to={`/compliance/contracts?scope=all&status=${s}`}>
            <Card className="flex flex-col">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted text-center">
                {STATUS_LABEL[s]}
              </div>
              <div className="text-3xl font-bold text-center mt-2 text-body">
                {statusCount[s] ?? 0}
              </div>
              <div className="flex justify-center mt-1">
                <span className={`px-2 py-0.5 text-xs font-medium rounded ${STATUS_BADGE[s]}`}>
                  {s}
                </span>
              </div>
              <div className={`h-1 mt-3 rounded ${STATUS_BAR[s]}`} />
            </Card>
          </Link>
        ))}
      </div>

      {/* Charts grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
            Contracts by Status
          </div>
          {statusPieData.length === 0 ? (
            <div className="text-sm text-gray-500 h-64 flex items-center justify-center">
              No contracts yet.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={statusPieData}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={90}
                  label
                >
                  {statusPieData.map((entry, i) => (
                    <Cell
                      key={entry.key}
                      fill={PIE_COLORS[i % PIE_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card>
          <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
            Contract Value by Category (Active, Top 8)
          </div>
          {categoryChartData.length === 0 ? (
            <div className="text-sm text-gray-500 h-64 flex items-center justify-center">
              No active contracts to aggregate.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={categoryChartData}>
                <XAxis dataKey="category" tick={{ fontSize: 12, fill: isDark ? '#9ca3af' : '#4b5563' }} />
                <YAxis tick={{ fontSize: 12, fill: isDark ? '#9ca3af' : '#4b5563' }} />
                <Tooltip
                  formatter={(v) =>
                    typeof v === 'number' ? formatValue(v) : String(v)
                  }
                />
                <Bar dataKey="value" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card>
          <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
            Expiring by Month (Next 6 Months)
          </div>
          {monthlyChartData.length === 0 ? (
            <div className="text-sm text-gray-500 h-64 flex items-center justify-center">
              Nothing expiring in the next 6 months.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={monthlyChartData}>
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: isDark ? '#9ca3af' : '#4b5563' }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: isDark ? '#9ca3af' : '#4b5563' }} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#f59e0b" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card>
          <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
            Contracts by Owner (Top 8)
          </div>
          {ownerChartData.length === 0 ? (
            <div className="text-sm text-gray-500 h-64 flex items-center justify-center">
              No contracts yet.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={ownerChartData} layout="vertical">
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: isDark ? '#9ca3af' : '#4b5563' }} />
                <YAxis dataKey="owner" type="category" tick={{ fontSize: 12, fill: isDark ? '#9ca3af' : '#4b5563' }} width={140} />
                <Tooltip />
                <Bar dataKey="count" fill="#8b5cf6" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {/* Expiring Soon table */}
      <Card>
        <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
          Expiring Soon (Next 10 Active)
        </div>
        {dashboard.expiring_soon.length === 0 ? (
          <div className="text-sm text-gray-500">No active contracts expiring.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted border-b border-gray-200 dark:border-gray-700">
                  <th className="py-2 pr-3">Title</th>
                  <th className="py-2 pr-3">Vendor</th>
                  <th className="py-2 pr-3">End Date</th>
                  <th className="py-2 pr-3">Value</th>
                  <th className="py-2 pr-3">Owner</th>
                  <th className="py-2 pr-3">Auto-Renewal</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.expiring_soon.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                    onClick={() => navigate(`/compliance/contracts?id=${r.id}`)}
                  >
                    <td className="py-2 pr-3 font-medium text-body">
                      {r.title}
                    </td>
                    <td className="py-2 pr-3">{r.third_party_name}</td>
                    <td className="py-2 pr-3">{r.end_date}</td>
                    <td className="py-2 pr-3">{formatValue(r.contract_value)}</td>
                    <td className="py-2 pr-3">{r.owner_display}</td>
                    <td className="py-2 pr-3">
                      {r.auto_renewal && (
                        <span className="px-2 py-0.5 text-xs rounded bg-info text-info-fg">
                          Auto
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Newest */}
      <Card>
        <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
          Recently Created
        </div>
        {dashboard.newest.length === 0 ? (
          <div className="text-sm text-gray-500">No contracts yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted border-b border-gray-200 dark:border-gray-700">
                  <th className="py-2 pr-3">Title</th>
                  <th className="py-2 pr-3">Vendor</th>
                  <th className="py-2 pr-3">End Date</th>
                  <th className="py-2 pr-3">Value</th>
                  <th className="py-2 pr-3">Owner</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.newest.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                    onClick={() => navigate(`/compliance/contracts?id=${r.id}`)}
                  >
                    <td className="py-2 pr-3 font-medium text-body">
                      {r.title}
                    </td>
                    <td className="py-2 pr-3">{r.third_party_name}</td>
                    <td className="py-2 pr-3">{r.end_date}</td>
                    <td className="py-2 pr-3">{formatValue(r.contract_value)}</td>
                    <td className="py-2 pr-3">{r.owner_display}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
